<template>
	<BaseCard
		:title="{ key: 'entity.notification.name', params: { count: 2 } }"
		divider="both"
		action-spacer
	>
		<template #append>
			<ButtonIcon
				v-if="totalLengthUnread > 0"
				:disabled="mode === 1"
				:loading="updating"
				:tooltip="{
					text: 'component.notifications.markAllAsRead',
					left: true,
				}"
				color="error"
				icon="fa-regular fa-check-double"
				icon-size="small"
				notext
				@click.stop="markAllAsRead"
			/>
			<ButtonIcon
				color="info"
				icon="fa-regular fa-times"
				icon-size="small"
				notext
				@click="$emit('close')"
			/>
		</template>

		<NotificationList
			v-model="tableId"
			:mode="mode"
			:height="height"
			:definition="definition"
		/>

		<template #actions>
			<v-btn-toggle v-model="mode" mandatory>
				<v-btn
					size="small"
					icon="fa-regular fa-bell-on"
					:title="t('component.notifications.unread')"
				/>
				<v-btn
					size="small"
					icon="fa-regular fa-bell-slash"
					:title="t('component.notifications.read')"
				/>
				<v-btn
					size="small"
					icon="fa-regular fa-bell"
					:title="t('component.notifications.all')"
				/>
			</v-btn-toggle>
			<v-spacer />
		</template>
	</BaseCard>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeMount, onBeforeUnmount } from 'vue'
import { useTable } from '@/composables/useTable'
import $collections from '@/constants/collections'
import { useNotificationStore } from '@/stores/modules/notification'
import BaseCard from '@/components/templates/Card/BaseCard.vue'
import NotificationList from './NotificationList.vue'
import { queryClient } from 'plugins/queryClient'
import { useI18n } from 'vue-i18n'

interface Props {
	width: number
	height: number
}

const { t } = useI18n()

const props = defineProps<Props>()
const emit = defineEmits<{
	close: []
}>()
const notificationStore = useNotificationStore()
const notificationsCountUnread = defineModel()
const tableId = ref()
const definition = computed(() => {
	switch (mode.value) {
		case 1:
			return $collections.notificationsRead
		case 2:
			return $collections.notificationsBase
		default:
			return $collections.notificationsUnread
	}
})

const mode = ref(0)
const updating = ref(false)
const polling = ref<NodeJS.Timeout>()

const notificationsDisabled = computed(() => {
	return typeof tableId.value === 'undefined'
})

const {
	itemsTotalLength: totalLengthUnread,
	isLoading: loadingUnread,
	invalidateData,
	reload,
} = useTable({
	tableId,
	disabled: notificationsDisabled,
})

async function markAllAsRead() {
	updating.value = true
	try {
		await notificationStore.markAllRead()
		await queryClient.invalidateQueries({ queryKey: ['notifications'] })
		await queryClient.invalidateQueries({ queryKey: ['read'] })
		await queryClient.invalidateQueries({ queryKey: ['unread'] })
		await reload()
	} catch (error) {
		console.error('Failed to mark all as read:', error)
	} finally {
		updating.value = false
	}
}
function startPolling() {
	if (!import.meta.env.DEV && !polling.value) {
		polling.value = setInterval(() => {
			invalidateData()
		}, 300000) // 5 minutes
	}
}
function stopPolling() {
	if (polling.value) {
		clearInterval(polling.value)
		polling.value = undefined
	}
}

watch(
	() => loadingUnread.value,
	(isLoading) => {
		if (!isLoading) {
			notificationsCountUnread.value = totalLengthUnread.value
		}
	}
)

onBeforeMount(() => {
	startPolling()
})

onBeforeUnmount(() => {
	stopPolling()
})

// Types
interface ProcessedNotification {
	id: string
	title: string
	icon: string
	content: {
		link?: {
			to?: any
			name: string
		}
		read_at?: string | null
		created_at?: string
	}
}
</script>
